import { useMemo, useRef } from 'react';
import { ownColumnVisibility } from './columnVisibility';

export interface SentVisibility {
  columnVisibilityModel: Record<string, boolean>;
  switched: string[];
}

export interface VisibilitySwitches {
  /** The grid laid its view out: the baseline a save tells the user's switches apart from. */
  layOut: (visibility: Record<string, boolean>, filled: readonly string[]) => void;
  /** The grid hid off-by-default columns that joined after the lay-out. */
  fillLate: (fields: readonly string[]) => void;
  /** What a save about to be sent stores and names as switched; the baseline moves to it at once. */
  send: (
    current: Record<string, boolean>,
    overridden: Readonly<Record<string, boolean>>,
    isView: boolean
  ) => SentVisibility;
  /** The save that sent this landed: the switches it was the last to name are confirmed. */
  landed: (sent: SentVisibility) => void;
}

/** Which of a grid's columns the user switched, across saves that may overlap, fail or answer out of order. */
export const useVisibilitySwitches = (): VisibilitySwitches => {
  const laidOutRef = useRef<Record<string, boolean>>({});
  const filledRef = useRef<Set<string>>(new Set());
  // Switches no save naming them has landed yet: every save names them again, and the last to name one confirms it.
  const pendingRef = useRef<Set<string>>(new Set());
  const lastNamedByRef = useRef<Map<string, SentVisibility>>(new Map());

  return useMemo(
    () => ({
      layOut: (visibility, filled) => {
        laidOutRef.current = { ...visibility };
        filledRef.current = new Set(filled);
        pendingRef.current.clear();
      },
      fillLate: (fields) => {
        fields.forEach((field) => {
          filledRef.current.add(field);
          laidOutRef.current[field] = false;
        });
      },
      send: (current, overridden, isView) => {
        const sent = ownColumnVisibility(
          current,
          laidOutRef.current,
          isView ? new Set<string>() : filledRef.current,
          overridden,
          pendingRef.current
        );
        // Moved on as the save is sent, not answered, or a switch back made meanwhile would match the old baseline.
        laidOutRef.current = { ...current };
        sent.switched.forEach((field) => {
          filledRef.current.delete(field);
          pendingRef.current.add(field);
          lastNamedByRef.current.set(field, sent);
        });
        return sent;
      },
      landed: (sent) => {
        sent.switched
          .filter((field) => lastNamedByRef.current.get(field) === sent)
          .forEach((field) => pendingRef.current.delete(field));
      }
    }),
    []
  );
};
